import { Scorm12API, Settings } from 'scorm-again';
import api from './api';
import { extractSavedScormProgress } from '../utils/scormProgress';

interface ScormProgress {
  lessonStatus: string;
  score: number;
  location: string;
  suspendData: string;
  totalTime: string;
  sessionTime: string;
  interactions: any[];
}

class ScormService {
  private scorm: Scorm12API;
  private courseId: string;
  private userId: string;
  private moduleId: string;

  constructor(courseId: string, userId: string, moduleId: string) {
    this.courseId = courseId;
    this.userId = userId;
    this.moduleId = moduleId;

    const settings: Settings = {
      autocommit: true,
      autocommitSeconds: 5,
      asyncCommit: true,
      sendFullCommit: true,
      autoProgress: true,
      logLevel: process.env.NODE_ENV === 'development' ? 'DEBUG' : 'NONE',
      selfReportSessionTime: true,
      alwaysSendTotalTime: true,
      strict_errors: false,
      xhrHeaders: {},
      xhrWithCredentials: true,
      fetchMode: 'cors',
      responseHandler: async (response) => {
        const data = await response.json();
        return { result: 'true', errorCode: 0, ...data };
      },
      requestHandler: (commitObject) => commitObject,
      onLogMessage: (messageLevel, logMessage) => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`[SCORM ${messageLevel}] ${logMessage}`);
        }
      }
    };

    this.scorm = new Scorm12API(settings);
  }

  async initialize(): Promise<boolean> {
    try {
      const savedProgress = await this.getProgress();

      if (savedProgress) {
        this.scorm.LMSSetValue('cmi.core.lesson_status', savedProgress.lessonStatus || 'not attempted');
        this.scorm.LMSSetValue('cmi.core.score.raw', String(savedProgress.score || 0));
        this.scorm.LMSSetValue('cmi.core.lesson_location', savedProgress.location || '');
        this.scorm.LMSSetValue('cmi.suspend_data', savedProgress.suspendData || '');
        this.scorm.LMSSetValue('cmi.core.total_time', savedProgress.totalTime || '0000:00:00');
        this.scorm.LMSSetValue('cmi.core.session_time', savedProgress.sessionTime || '0000:00:00');
      }

      return this.scorm.LMSInitialize('') === 'true';
    } catch (error) {
      console.error('Failed to initialize SCORM:', error);
      return false;
    }
  }

  async terminate(): Promise<boolean> {
    try {
      await this.saveProgress();
      return this.scorm.LMSFinish('') === 'true';
    } catch (error) {
      console.error('Failed to terminate SCORM:', error);
      return false;
    }
  }

  async saveProgress(): Promise<void> {
    try {
      const progress: ScormProgress = {
        lessonStatus: this.scorm.LMSGetValue('cmi.core.lesson_status') || 'not attempted',
        score: parseInt(this.scorm.LMSGetValue('cmi.core.score.raw') || '0'),
        location: this.scorm.LMSGetValue('cmi.core.lesson_location') || '',
        suspendData: this.scorm.LMSGetValue('cmi.suspend_data') || '',
        totalTime: this.scorm.LMSGetValue('cmi.core.total_time') || '0000:00:00',
        sessionTime: this.scorm.LMSGetValue('cmi.core.session_time') || '0000:00:00',
        interactions: this.getInteractions(),
      };

      await api.post('/scorm/progress', {
        moduleId: this.moduleId,
        courseId: this.courseId,
        scormData: progress,
      });
    } catch (error) {
      console.error('Failed to save SCORM progress:', error);
      throw error;
    }
  }

  private async getProgress(): Promise<ScormProgress | null> {
    try {
      const response = await api.get(`/scorm/progress/${this.moduleId}`);
      const saved = extractSavedScormProgress(response.data);

      if (!saved) {
        return null;
      }

      return {
        lessonStatus: saved.lessonStatus || 'not attempted',
        score: typeof saved.score === 'number' ? saved.score : 0,
        location: saved.location || '',
        suspendData: saved.suspendData || '',
        totalTime: saved.totalTime || '0000:00:00',
        sessionTime: saved.sessionTime || '0000:00:00',
        interactions: saved.interactions || [],
      };
    } catch (error: any) {
      if (error.response?.status === 404) return null;
      console.error('Failed to get SCORM progress:', error);
      return null;
    }
  }

  private getInteractions(): any[] {
    const interactions = [];
    const count = parseInt(this.scorm.LMSGetValue('cmi.interactions._count') || '0');

    for (let i = 0; i < count; i++) {
      interactions.push({
        id: this.scorm.LMSGetValue(`cmi.interactions.${i}.id`),
        type: this.scorm.LMSGetValue(`cmi.interactions.${i}.type`),
        timestamp: this.scorm.LMSGetValue(`cmi.interactions.${i}.timestamp`),
        result: this.scorm.LMSGetValue(`cmi.interactions.${i}.result`),
        latency: this.scorm.LMSGetValue(`cmi.interactions.${i}.latency`),
        student_response: this.scorm.LMSGetValue(`cmi.interactions.${i}.student_response`),
        correct_responses: this.scorm.LMSGetValue(`cmi.interactions.${i}.correct_responses`),
      });
    }

    return interactions;
  }

  getLessonStatus(): string {
    return this.scorm.LMSGetValue('cmi.core.lesson_status') || 'not attempted';
  }

  getScore(): number {
    return parseInt(this.scorm.LMSGetValue('cmi.core.score.raw') || '0');
  }

  getLocation(): string {
    return this.scorm.LMSGetValue('cmi.core.lesson_location') || '';
  }

  getValue(parameter: string): string {
    return this.scorm.LMSGetValue(parameter) || '';
  }

  setValue(parameter: string, value: string): boolean {
    return this.scorm.LMSSetValue(parameter, value) === 'true';
  }
}

export default ScormService;
